/**
 * ============================================================================
 * Material 模块 - 常量定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义枚举标签映射（中文显示）
 * - 定义权限常量
 * 
 * 📖 对应后端：
 * - Model: backend/app/Modules/Material/Models/Material.php (静态属性 $typeMap, $statusMap)
 * - Model: backend/app/Modules/Material/Models/MaterialUnit.php (静态属性 $typeMap, $unitNameMap)
 * 
 * ⚠️ 注意事项：
 * - 标签对象命名：{MODULE}_{ENUM}_LABELS（全大写）
 * - 权限常量命名：{MODULE}_PERMISSIONS（全大写）
 * - 使用 `as const` 保证类型推断
 * - 枚举标签的 key 必须与 enums.ts 中的枚举值对应
 * 
 * 🔄 更新时机：
 * - enums.ts 新增枚举值时（同步新增标签）
 * - 后端权限定义变化时
 * - 中文标签需要修改时
 * 
 * ✅ 验证方式：
 * 让AI执行以下检查：
 * 1. 读取 backend/app/Modules/Material/Models/Material.php
 * 2. 找到 public static $typeMap, public static $statusMap 等
 * 3. 检查本文件的 LABELS 对象是否包含所有映射
 * 4. 检查中文标签是否与后端一致
 * 
 * @module types/material/constants
 * @generated Manual (AI-assisted)
 * @last-updated 2025-10-30
 * ============================================================================
 */

import {
  MaterialType,
  MaterialStatus,
  MaterialUnitType,
  MaterialUnitCode,
  MaterialUnitStatus,
} from './enums'

/**
 * 物料类型标签映射
 * 
 * 对应后端: Material::$typeMap
 */
export const MATERIAL_TYPE_LABELS = {
  [MaterialType.RAW]: '原材料',
  [MaterialType.SEMI]: '半成品',
  [MaterialType.PRODUCT]: '成品',
  [MaterialType.AUX]: '辅料',
} as const

/**
 * 物料状态标签映射
 * 
 * 对应后端: Material::$statusMap
 */
export const MATERIAL_STATUS_LABELS = {
  [MaterialStatus.ACTIVE]: '启用',
  [MaterialStatus.INACTIVE]: '停用',
} as const

/**
 * 物料单位类型标签映射
 * 
 * 对应后端: MaterialUnit::$typeMap
 */
export const MATERIAL_UNIT_TYPE_LABELS = {
  [MaterialUnitType.BASE]: '基本单位',
  [MaterialUnitType.PURCHASE]: '采购单位',
  [MaterialUnitType.PRODUCTION]: '生产单位',
} as const

/**
 * 常用单位名称映射
 * 
 * 对应后端: MaterialUnit::$unitNameMap
 */
export const MATERIAL_UNIT_NAME_LABELS = {
  [MaterialUnitCode.PCS]: '个',
  [MaterialUnitCode.BOX]: '箱',
  [MaterialUnitCode.REEL]: '盘',
  [MaterialUnitCode.KG]: '千克',
  [MaterialUnitCode.T]: '吨',
  [MaterialUnitCode.G]: '克',
  [MaterialUnitCode.L]: '升',
  [MaterialUnitCode.ML]: '毫升',
  [MaterialUnitCode.M]: '米',
  [MaterialUnitCode.M2]: '平方米',
  [MaterialUnitCode.M3]: '立方米',
} as const

/**
 * 物料单位状态标签映射
 * 
 * 对应后端: MaterialUnit::STATUS_*
 */
export const MATERIAL_UNIT_STATUS_LABELS = {
  [MaterialUnitStatus.ACTIVE]: '启用',
  [MaterialUnitStatus.INACTIVE]: '停用',
} as const

/**
 * ⚠️ 物料权限常量已迁移至统一权限管理
 * 
 * 请从 @bayes-mes/shared 导入标准权限常量：
 * - PERM_MATERIAL_MATERIAL_VIEW
 * - PERM_MATERIAL_MATERIAL_CREATE
 * - PERM_MATERIAL_MATERIAL_UPDATE
 * - PERM_MATERIAL_MATERIAL_DELETE
 * - PERM_MATERIAL_MATERIAL_IMPORT
 * - PERM_MATERIAL_MATERIAL_EXPORT
 * - PERM_MATERIAL_MATERIAL_CONVERT
 * 
 * @deprecated 请使用 constants/api-permissions.ts 中的权限常量
 * @see bayes-mes-shared/constants/api-permissions.ts
 */

